import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useInfiniteList — cursor-paged list loader driven by an IntersectionObserver,
 * with a manual `loadMore` fallback (wire it to <LoadMoreButton/> so the list
 * still advances when the observer can't fire: reduced-motion, old browsers,
 * an observer error, or keyboard-only users).
 *
 * `loadPage(cursor)` must resolve to `{ items, nextCursor }`, where `nextCursor`
 * is falsy when there are no more pages. The cursor is opaque to the hook — it
 * can be a Firestore DocumentSnapshot, an id string, an offset, anything the
 * caller's loader understands. `loadPage` may be an inline function; the hook
 * always calls the latest one, so it never needs to be memoized to avoid
 * refetch loops. When filters/search change, call `reset()` to restart page 1.
 *
 * Returns { items, loading, hasMore, error, sentinelRef, loadMore, reset,
 *   setItems }. Attach `sentinelRef` to an element just below the list.
 * `setItems` is exposed for optimistic in-place updates (delete/acknowledge).
 */
export function useInfiniteList(loadPage, { rootMargin = "300px" } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const cursorRef = useRef(null);
  const loadingRef = useRef(false); // synchronous guard against concurrent loads
  const sentinelRef = useRef(null);

  // Always invoke the latest loader without making it a dependency (avoids
  // reset/refetch loops when the caller passes an inline function).
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await loadPageRef.current(cursorRef.current);
      const newItems = (res && res.items) || [];
      const nextCursor = (res && res.nextCursor) || null;
      setItems((prev) => [...prev, ...newItems]);
      cursorRef.current = nextCursor;
      setHasMore(!!nextCursor);
    } catch (e) {
      setError(e);
      // Stop the observer from hammering a failing loader; reset() re-arms it.
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [hasMore]);

  const reset = useCallback(() => {
    cursorRef.current = null;
    loadingRef.current = false;
    setItems([]);
    setError(null);
    setHasMore(true); // triggers the first-page effect below
  }, []);

  // Load page one on mount and after every reset() (items emptied, hasMore re-armed).
  useEffect(() => {
    if (items.length === 0 && hasMore && !loadingRef.current) {
      loadMore();
    }
  }, [items.length, hasMore, loadMore]);

  // Auto-advance when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) loadMore();
      },
      { rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loadMore, rootMargin]);

  return { items, loading, hasMore, error, sentinelRef, loadMore, reset, setItems };
}

export default useInfiniteList;
